/**
 * DimensionManager - Facade-facing API for dimensions.
 *
 * Controls what the next dimensions measure (X, Y or true distance), forces
 * the text of the selected dimension and sets its size.
 */

import { trackShapeChange } from "@/analytics/drawing-analytics";
import type { ScaleConfig, Textstyle } from "@/types/legend.types";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { SetDimensionSizeCommand } from "../commands/SetDimensionSizeCommand";
import { SetDimensionTextCommand } from "../commands/SetDimensionTextCommand";
import { getEntityHandle } from "../core/oda";
import {
  dimensionTextHeight,
  formatDimensionValue,
  getDimensionSize,
  getDimensionText,
  planDimensionSize,
  type DimensionKind,
} from "../dimensions";
import {
  getTransformableEntityType,
  TransformableEntityType,
} from "../entities/detection";
import { selectionService } from "../selection";
import type { DrawingModule } from "../types/DrawingWeb";

export class DimensionManager {
  constructor(private viewer: DRDrawingViewer) {}

  setKind(kind: DimensionKind): void {
    this.viewer.dimensionKind = kind;
  }

  getKind(): DimensionKind {
    return this.viewer.dimensionKind;
  }

  /** Whether a single dimension is selected. */
  canEditSelected(): boolean {
    return this.selectedDimension() !== null;
  }

  /**
   * Text of the selected dimension: its measured value as displayed, and
   * the text forced in its place ("" when none).
   */
  getSelectedText(): { measured: string; forced: string } | null {
    const id = this.selectedDimension();
    if (!id) return null;
    const text = getDimensionText(this.viewer.module, id);
    if (!text) return null;
    return {
      measured: formatDimensionValue(text.measurement),
      forced: text.forcedText,
    };
  }

  /**
   * Force the text of the selected dimension; an empty text shows the
   * measured value again. Undoable.
   */
  setSelectedText(text: string): boolean {
    const id = this.selectedDimension();
    if (!id) return false;
    const current = getDimensionText(this.viewer.module, id);
    if (!current || current.forcedText === text) return false;

    this.viewer.executeCommand(
      new SetDimensionTextCommand(this.viewer, {
        entityHandle: getEntityHandle(id),
        fromText: current.forcedText,
        toText: text,
      }),
    );
    trackShapeChange("dimension_text_forced", { forced: text !== "" });
    this.viewer.update();
    return true;
  }

  /** Text height of the selected dimension. */
  getSelectedTextHeight(): number | null {
    const id = this.selectedDimension();
    if (!id) return null;
    const size = getDimensionSize(this.viewer.module, id);
    return size ? dimensionTextHeight(size) : null;
  }

  /**
   * Set the text height of the selected dimension; the arrows follow.
   * Undoable.
   */
  setSelectedTextHeight(height: number): boolean {
    return this.resizeSelected(height);
  }

  /**
   * Give the selected dimension the font and height of a scale, keeping
   * its current height when the scale has none. Undoable.
   */
  setSelectedScale(config: ScaleConfig): boolean {
    return this.resizeSelected(
      config.props.textsize || config.props.textstyle?.size || undefined,
      config.props.textstyle,
    );
  }

  private selectedDimension(): DrawingModule.OdDbObjectId | null {
    const ids = selectionService.getAll();
    if (ids.length !== 1) return null;
    const type = getTransformableEntityType(this.viewer.module, ids[0]);
    return type === TransformableEntityType.Dimension ? ids[0] : null;
  }

  private resizeSelected(height?: number, font?: Textstyle): boolean {
    const id = this.selectedDimension();
    const db = this.viewer.database;
    if (!id || !db) return false;
    const current = getDimensionSize(this.viewer.module, id);
    if (!current) return false;

    const fromHeight = dimensionTextHeight(current);
    const toHeight = height ?? fromHeight;
    if (!(toHeight > 0)) return false;

    const to = planDimensionSize(
      this.viewer.module,
      db,
      current,
      toHeight,
      font,
    );
    if (
      to.dimscale === current.dimscale &&
      to.dimtxt === current.dimtxt &&
      to.textStyleHandle === current.textStyleHandle
    ) {
      return false;
    }

    this.viewer.executeCommand(
      new SetDimensionSizeCommand(this.viewer, {
        entityHandle: getEntityHandle(id),
        from: current,
        to,
        fromHeight,
        toHeight,
      }),
    );
    trackShapeChange("dimension_resized");
    this.viewer.update();
    return true;
  }
}
